import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, contentChild, input, output, TemplateRef } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { ColorSchemeService } from '@examdojo/browser/color-scheme';
import { IconComponent } from '@examdojo/core/icon';
import { AppLogoComponent } from '@examdojo/ui/app-logo';
import { distinctUntilDeepChanged } from '@examdojo/core/rxjs';
import { PlatformService } from '@examdojo/platform';
import { UserQuery } from '@examdojo/user';
import { TranslocoPipe } from '@jsverse/transloco';
import { combineLatest, distinctUntilChanged, filter, map, shareReplay, startWith } from 'rxjs';
import { SidebarProfileLinkComponent } from './sidebar-profile-link/sidebar-profile-link.component';
import { SidenavItem } from './sidenav.model';

interface SidenavRouteData {
  compactView: boolean;
  hideSidenav: boolean;
}

function getSidenavRouteData(router: Router): SidenavRouteData {
  const shellRoute = router.routerState.root.firstChild;

  if (!shellRoute) {
    return { compactView: false, hideSidenav: false };
  }

  let current = shellRoute.snapshot.firstChild;
  let compactView: boolean | undefined;
  let hideSidenav: boolean | undefined;

  while (current) {
    if (current.data?.['compactView'] !== undefined) {
      compactView = current.data['compactView'];
    }
    if (current.data?.['hideSidenav'] !== undefined) {
      hideSidenav = current.data['hideSidenav'];
    }
    current = current.firstChild;
  }

  return {
    compactView: compactView ?? false,
    hideSidenav: hideSidenav ?? false,
  };
}

@Component({
  selector: 'dojo-sidenav',
  imports: [
    NgTemplateOutlet,
    RouterLink,
    TranslocoPipe,
    RouterLinkActive,
    MatSidenavModule,
    IconComponent,
    NgClass,
    SidebarProfileLinkComponent,
    AppLogoComponent,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'h-full flex flex-col',
    '[class.sidenav-hidden]': 'state.isHidden',
  },
})
export class SidenavComponent {
  constructor(
    private readonly userQuery: UserQuery,
    private readonly router: Router,
    private readonly colorSchemeService: ColorSchemeService,
    private readonly platformService: PlatformService,
  ) {}

  readonly COMPACT_WIDTH = 80;
  readonly NORMAL_WIDTH = 240;

  readonly items = input.required<SidenavItem[]>();
  readonly logoCompact = input.required<string>();
  readonly logoCompactDark = input<string>();
  readonly logoFull = input.required<string>();
  readonly logoFullDark = input<string>();
  readonly logoAlt = input<string>('Logo');
  readonly settingsRoute = input.required<string>();
  readonly hasMobileTopPanel = input(false);
  readonly showMobileMoreButton = input(true);
  readonly isMoreMenuOpen = input<boolean>(false);

  readonly moreClicked = output<void>();
  readonly menuItemClicked = output<void>();

  readonly customFooter = contentChild<TemplateRef<unknown>>('sidenavFooter');
  readonly mobileTopPanel = contentChild<TemplateRef<unknown>>('mobileTopPanel');
  readonly customLogo = contentChild<TemplateRef<unknown>>('customLogo');

  readonly isMobile$ = this.platformService.isSmall$.pipe(distinctUntilChanged());

  private readonly sidenavRouteData$ = this.router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    map(() => getSidenavRouteData(this.router)),
    startWith({ compactView: false, hideSidenav: false }),
    distinctUntilDeepChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly isCompact$ = combineLatest([
    this.platformService.resize$.pipe(
      startWith(null),
      map(() => this.platformService.width < 1100),
      distinctUntilChanged(),
    ),
    this.sidenavRouteData$.pipe(
      map((data) => data.compactView),
      distinctUntilChanged(),
    ),
    this.isMobile$,
  ]).pipe(
    map(([isScreenSmall, isCompactView, isMobile]) => {
      if (isMobile) {
        return false;
      }
      return isScreenSmall || isCompactView;
    }),
  );

  readonly isHidden$ = this.sidenavRouteData$.pipe(
    map((data) => data.hideSidenav),
    distinctUntilChanged(),
  );

  readonly state = connectState({
    user: this.userQuery.active$,
    darkMode: this.colorSchemeService.hasDarkMode$,
    isMobile: this.isMobile$,
    isCompact: this.isCompact$,
    isHidden: this.isHidden$,
  });

  navigateToProfile() {
    this.router.navigate([this.settingsRoute()]);
  }

  onMenuItemClick() {
    this.menuItemClicked.emit();
  }

  onMoreClick() {
    this.moreClicked.emit();
  }
}
